import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { apiService } from "../../utils/apiService";
import { User } from "../../types";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Megaphone,
  Trash2,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Users,
  Save,
  Send,
} from "lucide-react";

interface Announcement {
  id: number;
  title: string;
  content: string;
  author_name: string;
  target_role: string;
  created_at: string;
  created_by: string;
}

interface AnnouncementBoardProps {
  currentUser: User;
}

const AVAILABLE_ROLES = ["admin", "supervisor", "fabricator", "client"];

const swalCustomClasses = {
  container: "swal-container",
  popup: "swal-popup",
  title: "swal-title",
  htmlContainer: "swal-content",
  confirmButton: "swal-confirm-button",
  cancelButton: "swal-cancel-button",
  icon: "swal-icon",
};

export function AnnouncementBoard({ currentUser }: AnnouncementBoardProps) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>(["all"]);

  const canPost = ["admin", "supervisor"].includes(currentUser.role);
  const isAdmin = currentUser.role === "admin";

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await apiService.getAnnouncements();
      if (res.data) {
        setAnnouncements(res.data);
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error("Failed to load announcements", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? announcements.length - 1 : prev - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === announcements.length - 1 ? 0 : prev + 1,
    );
  };

  const toggleRole = (role: string) => {
    if (role === "all") {
      setSelectedRoles(["all"]);
      return;
    }

    let nextRoles = selectedRoles.filter((entry) => entry !== "all");

    if (nextRoles.includes(role)) {
      nextRoles = nextRoles.filter((entry) => entry !== role);
    } else {
      nextRoles.push(role);
    }

    if (nextRoles.length === 0) nextRoles = ["all"];
    setSelectedRoles(nextRoles);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setTitle("");
    setContent("");
    setSelectedRoles(["all"]);
    setShowModal(true);
  };

  const openEditModal = (announcement: Announcement) => {
    setEditingId(announcement.id);
    setTitle(announcement.title);
    setContent(announcement.content);

    try {
      const parsedRoles = JSON.parse(announcement.target_role);
      setSelectedRoles(Array.isArray(parsedRoles) ? parsedRoles : ["all"]);
    } catch {
      setSelectedRoles(["all"]);
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete",
        text: "Title and content are required.",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (title.length > 50) {
      Swal.fire({
        icon: "warning",
        title: "Title Exceeds Limit",
        text: "Title must be less than 50 characters.",
        customClass: swalCustomClasses,
      });
      return;
    }

    if (content.length > 1000) {
      Swal.fire({
        icon: "warning",
        title: "Content Exceeds Limit",
        text: "Content must be less than 1000 characters.",
        customClass: swalCustomClasses,
      });
      return;
    }

    const isEdit = editingId !== null;
    const actionText = isEdit ? "update" : "post";
    const successText = isEdit ? "Updated!" : "Posted!";

    const result = await Swal.fire({
      title: `${isEdit ? "Update" : "Post"} Announcement?`,
      text: `Are you sure you want to ${actionText} this announcement?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Update" : "Post",
      cancelButtonText: "Cancel",
      customClass: swalCustomClasses,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: isEdit ? "Updating..." : "Posting...",
      text: "Please wait...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: swalCustomClasses,
    });

    const startTime = Date.now();
    const minDuration = 1000;

    try {
      if (editingId !== null) {
        await apiService.updateAnnouncement(editingId, {
          title,
          content,
          targetRoles: selectedRoles,
        });
      } else {
        await apiService.createAnnouncement({
          title,
          content,
          targetRoles: selectedRoles,
        });
      }

      const elapsed = Date.now() - startTime;
      if (elapsed < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
      }

      Swal.close();

      Swal.fire({
        icon: "success",
        title: successText,
        timer: 1200,
        showConfirmButton: false,
        customClass: swalCustomClasses,
      });

      closeModal();
      fetchAnnouncements();
    } catch (error) {
      const elapsed = Date.now() - startTime;
      if (elapsed < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
      }

      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to ${actionText} announcement.`,
        customClass: swalCustomClasses,
      });
    }
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "Delete Announcement?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      customClass: swalCustomClasses,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Deleting...",
      text: "Please wait...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: swalCustomClasses,
    });

    const startTime = Date.now();
    const minDuration = 1000;

    try {
      await apiService.deleteAnnouncement(id);

      const nextAnnouncements = announcements.filter((announcement) => announcement.id !== id);
      setAnnouncements(nextAnnouncements);

      if (currentIndex >= nextAnnouncements.length) {
        setCurrentIndex(Math.max(0, nextAnnouncements.length - 1));
      }

      const elapsed = Date.now() - startTime;
      if (elapsed < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
      }

      Swal.close();

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Announcement has been removed.",
        timer: 1200,
        showConfirmButton: false,
        customClass: swalCustomClasses,
      });
    } catch (error) {
      const elapsed = Date.now() - startTime;
      if (elapsed < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
      }

      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Could not delete announcement.",
        customClass: swalCustomClasses,
      });
    }
  };

  const renderTargetBadges = (roleString: string) => {
    let roles: string[] = [];

    try {
      roles = JSON.parse(roleString);
    } catch {
      return null;
    }

    if (roles.includes("all")) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {roles.map((role) => (
          <span
            key={role}
            className="inline-flex items-center px-2 py-0.5 rounded-md border border-slate-300 dark:border-[#232730] bg-slate-100 dark:bg-[#1A1D24] text-[10px] font-semibold uppercase text-slate-700 dark:text-zinc-300"
          >
            {role}
          </span>
        ))}
      </div>
    );
  };

  const currentAnnouncement = announcements[currentIndex];

  return (
    <>
      <Card className="relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-[#232730] bg-white dark:bg-[#121418] p-4 sm:p-6 shadow-sm dark:shadow-xl transition-all">
        <CardHeader className="flex w-full flex-col gap-3.5 border-b border-slate-100 dark:border-[#232730] p-0 pb-3.5 sm:pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full items-center gap-3 sm:w-auto">
            <div className="flex h-9.5 w-9.5 sm:h-10 sm:w-10 items-center justify-center rounded-lg border border-slate-200 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] text-slate-900 dark:text-white shadow-xs shrink-0">
              <Megaphone className="h-4.5 w-4.5 sm:h-5 sm:w-5 text-slate-700 dark:text-zinc-200" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg font-bold tracking-tight text-slate-900 dark:text-white font-display">
                Announcements
              </CardTitle>
              <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium truncate">
                Team updates and broadcast notices
              </p>
            </div>
            {!loading && announcements.length > 0 && (
              <span className="ml-auto flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] px-2 py-0.5 sm:px-2.5 sm:py-1 text-xs font-mono font-medium text-slate-700 dark:text-zinc-300">
                {currentIndex + 1} / {announcements.length}
              </span>
            )}
          </div>

          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
            {announcements.length > 1 && (
              <div className="mr-1 flex items-center rounded-lg border border-slate-200 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] p-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-md text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-[#232730] cursor-pointer"
                  onClick={handlePrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="h-4 w-[1px] bg-slate-200 dark:bg-[#232730]" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-md text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-[#232730] cursor-pointer"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {canPost && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-lg border-slate-300 dark:border-[#232730] bg-white dark:bg-[#121418] text-xs font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-[#1A1D24] cursor-pointer"
                onClick={openCreateModal}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New Notice
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="relative flex w-full min-h-0 min-w-0 flex-1 flex-col px-0 pt-4">
          {loading ? (
            <div className="flex h-full items-center justify-center text-xs text-slate-500 dark:text-zinc-400">
              Loading notices...
            </div>
          ) : announcements.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 dark:border-[#232730] bg-slate-50/50 dark:bg-[#0E1014]/40 p-8 text-center text-xs text-slate-500 dark:text-zinc-400">
              <Megaphone className="mb-2 h-7 w-7 opacity-30 text-slate-400 dark:text-zinc-500" />
              <p>No broadcast announcements yet.</p>
            </div>
          ) : (
            <div className="group relative flex h-full w-full flex-col rounded-xl border border-slate-200/80 dark:border-[#232730] bg-slate-50/50 dark:bg-[#0E1014]/60 p-5">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-base font-bold font-sans leading-snug text-slate-900 dark:text-white">
                    {currentAnnouncement.title}
                  </h4>
                  <div className="mt-1 text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-zinc-400 font-mono">
                    {currentAnnouncement.author_name} •{" "}
                    {format(new Date(currentAnnouncement.created_at), "MMM d, yyyy")}
                  </div>
                </div>
              </div>

              <div className="mb-4 flex-1 overflow-y-auto pr-1">
                <p className="whitespace-pre-wrap text-xs sm:text-sm leading-relaxed text-slate-700 dark:text-zinc-300">
                  {currentAnnouncement.content}
                </p>
              </div>

              <div className="mt-auto flex items-center justify-between border-t border-slate-200 dark:border-[#232730] pt-3">
                <div className="flex-1">
                  {renderTargetBadges(currentAnnouncement.target_role)}
                </div>

                {(isAdmin || currentUser.id === currentAnnouncement.created_by) && (
                  <div className="flex gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-md p-0 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-[#232730] hover:text-slate-900 dark:hover:text-white cursor-pointer"
                      onClick={() => openEditModal(currentAnnouncement)}
                      title="Edit"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-md p-0 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-600 cursor-pointer"
                      onClick={() => handleDelete(currentAnnouncement.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showModal &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in">
            <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 dark:border-[#232730] bg-white dark:bg-[#121418] p-6 shadow-2xl animate-in zoom-in-95">
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4 rounded-md text-slate-400 hover:text-slate-900 dark:hover:text-white cursor-pointer"
                onClick={closeModal}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="mb-5">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white font-display">
                  {editingId ? "Edit Announcement" : "New Announcement"}
                </h2>
                <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium">
                  Share operational and company broadcasts with your team
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-800 dark:text-zinc-200">
                    Title
                  </label>
                  <Input
                    placeholder="e.g. Schedule Update or Maintenance Notice"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="h-10 rounded-lg border border-slate-300 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] text-slate-900 dark:text-white text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-800 dark:text-zinc-200">
                    Content
                  </label>
                  <Textarea
                    placeholder="Write announcement details..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="min-h-[120px] rounded-lg border border-slate-300 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] text-slate-900 dark:text-white text-xs leading-relaxed"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-zinc-200">
                    <Users className="h-3.5 w-3.5" /> Target Audience
                  </label>
                  <div className="flex flex-wrap gap-1.5 rounded-lg border border-slate-300 dark:border-[#232730] bg-slate-50 dark:bg-[#0E1014] p-2.5">
                    <button
                      type="button"
                      onClick={() => toggleRole("all")}
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                        selectedRoles.includes("all")
                          ? "bg-slate-950 dark:bg-white text-white dark:text-[#08090B]"
                          : "bg-slate-200 dark:bg-[#1A1D24] text-slate-700 dark:text-zinc-300 hover:bg-slate-300 dark:hover:bg-[#232730]"
                      }`}
                    >
                      Everyone
                    </button>
                    {AVAILABLE_ROLES.map((role) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleRole(role)}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold capitalize transition-all cursor-pointer ${
                          selectedRoles.includes(role)
                            ? "bg-slate-950 dark:bg-white text-white dark:text-[#08090B]"
                            : "bg-slate-200 dark:bg-[#1A1D24] text-slate-700 dark:text-zinc-300 hover:bg-slate-300 dark:hover:bg-[#232730]"
                        }`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="h-10 rounded-lg border-slate-300 dark:border-[#232730] bg-white dark:bg-[#121418] text-xs font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-[#1A1D24] cursor-pointer"
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="h-10 rounded-lg bg-slate-950 dark:bg-white text-white dark:text-[#08090B] hover:bg-slate-800 dark:hover:bg-zinc-200 text-xs font-bold shadow-md cursor-pointer"
                    onClick={handleSubmit}
                  >
                    {editingId ? (
                      <Save className="mr-1.5 h-3.5 w-3.5" />
                    ) : (
                      <Send className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {editingId ? "Update Notice" : "Broadcast Announcement"}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
